import axios from "axios";
import store from "@/store";
import LoadingManager from "@/services/loadingManager";

const api = axios.create({
  baseURL: process.env.VUE_APP_API_URL || "http://localhost:8081/api/v1",
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Track how many requests are in-flight — show loader on first, hide on last
let activeRequests = 0;

// Request interceptor — attach JWT token + start global loader
api.interceptors.request.use(
  (config) => {
    const token = store.getters["auth/token"];
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
      delete config.headers["content-type"];
    }

    // Skip loader for silent requests
    if (!config._silent) {
      activeRequests++;
      if (activeRequests === 1) {
        LoadingManager.start("fullscreen");
      }
    }

    return config;
  },
  (error) => {
    // Ensure counter stays consistent on request error
    if (!error.config?._silent && activeRequests > 0) {
      activeRequests--;
      if (activeRequests === 0) LoadingManager.end("fullscreen");
    }
    return Promise.reject(error);
  }
);

// Response interceptor — handle 401 with refresh token + end global loader
api.interceptors.response.use(
  (response) => {
    // Decrement counter and hide loader when all requests complete
    if (!response.config?._silent && activeRequests > 0) {
      activeRequests--;
      if (activeRequests === 0) LoadingManager.end("fullscreen");
    }
    return response.data; // Unwrap so callers get { success, data } directly
  },
  async (error) => {
    const originalRequest = error.config;

    // Decrement counter on error response
    if (!originalRequest?._silent && activeRequests > 0) {
      activeRequests--;
      if (activeRequests === 0) LoadingManager.end("fullscreen");
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        await store.dispatch("auth/refreshToken");
        const newToken = store.getters["auth/token"];
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch {
        store.dispatch("auth/logout");
        return Promise.reject(error);
      }
    }

    const message =
      error.response?.data?.message || error.message || "An error occurred";
    return Promise.reject(new Error(message));
  }
);

export default api;
